import { z } from 'zod';

// The club's clock (D10, D12, D32): the club meets in North Cyprus, so its dates and times are
// Asia/Nicosia's, while the database keeps instants in UTC. A weekly series keeps its local time
// (19:00 stays 19:00 when the clocks change), and the boards' weeks run Monday to Sunday here.
// Everything below reads the zone through Intl with the zone named, never the machine's own: the
// PC runs in Asia/Nicosia, GitHub's runners and a test with TZ=UTC in UTC, and they must agree.

/** The club's time zone: its trainings' times, its days and weeks (D10, D12). */
export const CLUB_TIME_ZONE = 'Asia/Nicosia';

const DAY_MS = 24 * 60 * 60 * 1000;

function parts(date: string): [number, number, number] {
  const [year, month, day] = date.split('-').map(Number);
  return [year ?? NaN, month ?? NaN, day ?? NaN];
}

/** A date as the calendar reads it, no zone: the number of days since 1970-01-01, in ms. */
function dayStart(date: string): number {
  const [year, month, day] = parts(date);
  return Date.UTC(year, month - 1, day);
}

function dateOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** A calendar date, `2026-10-27`: a day that exists (no 30 February). */
export const LocalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  // Zod runs this even when the pattern fails, so it mustn't throw on what isn't a date at all.
  .refine((date) => {
    const ms = dayStart(date);
    return Number.isFinite(ms) && dateOf(ms) === date;
  }, 'no such day');
export type LocalDate = z.infer<typeof LocalDate>;

/** A time of day on the club's clock, `19:00`: 24 hours, to the minute. */
export const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export type LocalTime = z.infer<typeof LocalTime>;

/** The date `days` days after (or, negative, before) `date`. */
export function addDays(date: string, days: number): string {
  const [year, month, day] = parts(date);
  return dateOf(Date.UTC(year, month - 1, day + days));
}

/** The day of the week, ISO's way: 1 is Monday, 7 is Sunday (D10's weeks start on Monday). */
export function isoWeekday(date: string): number {
  return ((new Date(dayStart(date)).getUTCDay() + 6) % 7) + 1;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string): Intl.DateTimeFormat {
  let found = formatters.get(zone);
  if (!found) {
    found = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(zone, found);
  }
  return found;
}

/**
 * The wall clock in `zone` at an instant, written as the instant at which a clock in UTC reads the
 * same, so the zone's offset at the instant is `wallClock(ms) - ms`. In whole seconds: Intl gives
 * no milliseconds.
 */
function wallClock(ms: number, zone: string): number {
  const read: Record<string, number> = {};
  for (const part of formatter(zone).formatToParts(new Date(ms)))
    read[part.type] = Number(part.value);
  const { year = NaN, month = NaN, day = NaN, hour = NaN, minute = NaN, second = NaN } = read;
  // `% 24`: an older ICU says 24 at midnight even in the 23-hour cycle.
  return Date.UTC(year, month - 1, day, hour % 24, minute, second);
}

/**
 * The instant at which the club's clock (or `zone`'s) reads `time` on `date`. Where the clocks go
 * forward (Asia/Nicosia: the last Sunday of March, 03:00 becomes 04:00) a time in the hour that's
 * skipped moves forward by that hour, so 03:30 is 04:30 summer time; where they go back (the last
 * Sunday of October, 04:00 becomes 03:00) a time in the hour that comes twice is the first of the
 * two, in summer time. RFC 5545's rule for calendars, and JavaScript's Temporal's "compatible".
 */
export function toInstant(date: string, time: string, zone: string = CLUB_TIME_ZONE): Date {
  const [hour, minute] = time.split(':').map(Number);
  const [year, month, day] = parts(date);
  const wall = Date.UTC(year, month - 1, day, hour ?? NaN, minute ?? NaN);
  if (Number.isNaN(wall)) throw new RangeError(`not a date and time: ${date} ${time}`);
  // The zone's offsets a day either side: the same on most days, the two of a switch on its day.
  const before = wallClock(wall - DAY_MS, zone) - (wall - DAY_MS);
  const after = wallClock(wall + DAY_MS, zone) - (wall + DAY_MS);
  const fits = [...new Set([before, after])]
    .map((offset) => wall - offset)
    .filter((instant) => wallClock(instant, zone) === wall);
  // Two fit in the hour that comes twice: the earlier is the first time the clock reads it.
  if (fits.length > 0) return new Date(Math.min(...fits));
  // None fits in the hour that's skipped: read it with the offset from before the switch.
  return new Date(wall - before);
}

/** The date and the time of day (`HH:MM`) the club's clock (or `zone`'s) reads at an instant. */
export function toLocal(
  instant: Date | number | string,
  zone: string = CLUB_TIME_ZONE,
): { date: string; time: string } {
  const ms = new Date(instant).getTime();
  if (Number.isNaN(ms)) throw new RangeError(`not an instant: ${String(instant)}`);
  const wall = new Date(wallClock(ms, zone)).toISOString();
  return { date: wall.slice(0, 10), time: wall.slice(11, 16) };
}
